import { useMemo } from 'react';
import { css } from '@emotion/react';
import { OpeningRequest, ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import dayjs from 'dayjs';
import type { AppointmentType } from '@frontend/api-appointment-types';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { useLocationTimezone } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import { Chip, DatePickerField, DropdownField, FormRow, Text, TimeField, numeric } from '@frontend/design-system';
import { getLabelWithTimezoneAbbr } from '../../utils';
import { useScheduleRequestModalContext } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/Context/ScheduleRequestModalContext';
import { useManageScheduleRequestModalForm } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/useManageScheduleRequestModalForm';
import {
  ReducedAppointmentType,
  ReducedOperatory,
  ReducedPractitioner,
} from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/useReviewRequest';
import {
  SourceTenantListItem,
  WorkstationDropdownList,
} from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/ScheduleRequestModal/types';
import { getDurationInMinutes } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/utils';
import { DropdownOptionLabel } from '../dropdown-field-option';
import { ReadOnlyField } from '../ReadOnlyReviewAppointmentRequestForm/ReadOnlyField';
import { AppointmentStatusMappingBanner } from './appointment-status-mapping-banner';
import { Card } from './Card';
import { WorkstationDropdownSearchList } from './workstation-dropdown-search-list';

type Props = {
  appointmentTypes: AppointmentType[] | ReducedAppointmentType[];
  disabledForm?: boolean;
  getFieldProps: ReturnType<typeof useManageScheduleRequestModalForm>['scheduleRequestApprovalForm']['getFieldProps'];
  values: ReturnType<typeof useManageScheduleRequestModalForm>['scheduleRequestApprovalForm']['values'];
  dateField: ReturnType<typeof useManageScheduleRequestModalForm>['customDateField'];
  startTimeField: ReturnType<typeof useManageScheduleRequestModalForm>['customStartTimeField'];
  endTimeField: ReturnType<typeof useManageScheduleRequestModalForm>['customEndTimeField'];
  selectedScheduleRequest: ScheduleRequest | undefined;
  showSourceLocationDropdown?: boolean;
  providerList: ReducedPractitioner[];
  workstationList: WorkstationDropdownList<ReducedOperatory>;
  isLoadingAvailableWorkstations?: boolean;
  showGroupedWorkstations?: boolean;
  appointmentWritebackSourceTenants?: SourceTenantListItem[];
  showWorkstationDropdown: boolean;
  isPaymentsEnabled?: boolean;
  paidAmount?: number;
};

export const AppointmentDataCardV3 = ({
  getFieldProps,
  appointmentTypes,
  selectedScheduleRequest,
  values,
  dateField,
  startTimeField,
  endTimeField,
  showSourceLocationDropdown,
  providerList,
  workstationList,
  showGroupedWorkstations,
  isLoadingAvailableWorkstations,
  appointmentWritebackSourceTenants,
  showWorkstationDropdown,
  isPaymentsEnabled,
  paidAmount,
}: Props) => {
  const { t } = useTranslation('scheduleCalendarRequest');
  const { formatDateAndTime, locationTimezoneAbbr } = useLocationTimezone(selectedScheduleRequest?.locationId);
  const { isScheduleRequestFormDisabled } = useScheduleRequestModalContext();

  const isEndTimeNextDay = useMemo(() => {
    const startTime = dayjs(startTimeField.value, 'HH:mm:ss');
    const endTime = dayjs(endTimeField.value, 'HH:mm:ss');
    return startTime.isAfter(endTime) && endTimeField.value !== '00:00:00';
  }, [endTimeField.value, startTimeField.value]);

  const hasServiceType = appointmentTypes.some((apptType) => 'bookableAppointmentTypeId' in apptType);

  const selectedSourceTenant = useMemo(
    () => appointmentWritebackSourceTenants?.find(({ sourceTenantId }) => sourceTenantId === values.source),
    [appointmentWritebackSourceTenants, values.source]
  );

  return (
    <Card title={t('Appointment Data')}>
      <form>
        {isPaymentsEnabled && (
          <FormRow>
            <ReadOnlyField
              label={t('Paid')}
              value={
                paidAmount ? (
                  <Chip variant='warn' leftElement={<Icon name='dollar-sign-small' />}>
                    {numeric(paidAmount, { decimals: 2 })}
                  </Chip>
                ) : (
                  <>-</>
                )
              }
            />
          </FormRow>
        )}
        {showSourceLocationDropdown && (
          <FormRow>
            <DropdownField
              css={sourceTextStyles}
              disabled={isScheduleRequestFormDisabled}
              label={t('Location')}
              {...getFieldProps('source')}
            >
              {appointmentWritebackSourceTenants?.map(({ sourceTenantId = '', name, pmsName }) => (
                <DropdownField.Option css={sourceTextStyles} key={sourceTenantId} value={sourceTenantId}>
                  <span className='text'>{name}</span>
                  <span className='small-text'>{pmsName}</span>
                </DropdownField.Option>
              ))}
            </DropdownField>
          </FormRow>
        )}
        <AppointmentStatusMappingBanner
          locationId={selectedScheduleRequest?.locationId}
          sourceId={selectedSourceTenant?.sourceId}
          locationName={selectedSourceTenant?.name}
          pmsName={selectedSourceTenant?.pmsName}
        />
        <FormRow cols={[50, 50]}>
          <DropdownField
            css={fieldStyles}
            disabled={isScheduleRequestFormDisabled}
            label={hasServiceType ? t('Service') : t('Appointment Type')}
            {...getFieldProps('appointmentType')}
          >
            {appointmentTypes?.map((item: AppointmentType | ReducedAppointmentType) => {
              const id = 'bookableAppointmentTypeId' in item ? item.bookableAppointmentTypeId ?? item.id : item.id;
              return (
                <DropdownField.Option key={id} value={id} disabled={!item.active}>
                  <DropdownOptionLabel
                    disabled={isScheduleRequestFormDisabled}
                    label={`${item.name} (${t('{{count}} mins', {
                      count: getDurationInMinutes(item.durationMinutes),
                    })} )`}
                    archived={!item.active}
                  />
                </DropdownField.Option>
              );
            })}
          </DropdownField>
          <DropdownField
            css={fieldStyles}
            disabled={isScheduleRequestFormDisabled}
            label={t('Select Provider')}
            {...getFieldProps('provider')}
          >
            {providerList.map((provider) => (
              <DropdownField.Option
                key={provider.id}
                value={provider.id ?? ''}
                disabled={!provider.isActive || provider.hidden}
              >
                <DropdownOptionLabel
                  label={getFullName(provider)}
                  archived={!provider.isActive}
                  hidden={provider.hidden}
                  disabled={isScheduleRequestFormDisabled}
                />
              </DropdownField.Option>
            ))}
          </DropdownField>
        </FormRow>
        {showWorkstationDropdown && (
          <FormRow>
            <WorkstationDropdownSearchList
              disabled={isScheduleRequestFormDisabled}
              fieldProps={getFieldProps('workstation')}
              isLoadingAvailableWorkstations={isLoadingAvailableWorkstations}
              showGroupedWorkstations={showGroupedWorkstations}
              workstationList={workstationList}
            />
          </FormRow>
        )}
        <FormRow>
          <DropdownField
            disabled={isScheduleRequestFormDisabled}
            label={t('Date & Time')}
            {...getFieldProps('dateAndTime')}
          >
            {selectedScheduleRequest?.requestedOpenings?.map((item: OpeningRequest) => {
              const isCurrentYear = dayjs(item?.dateTime).isSame(dayjs(), 'year');
              const format = isCurrentYear ? 'MMM D @ hh:mm a' : 'MMM D YYYY @ hh:mm a';
              const date = formatDateAndTime(item?.dateTime, format, true);
              return (
                <DropdownField.Option key={item?.dateTime} value={item?.dateTime ?? ''}>
                  {date}
                </DropdownField.Option>
              );
            })}
            <DropdownField.Option value='custom'>Custom</DropdownField.Option>
          </DropdownField>
        </FormRow>
        {values.dateAndTime === 'custom' && (
          <>
            <FormRow cols={[100]}>
              <DatePickerField
                name='customDate'
                label={getLabelWithTimezoneAbbr(t('Date'), locationTimezoneAbbr)}
                css={{ maxWidth: '100%' }}
                {...dateField}
              />
            </FormRow>
            <FormRow cols={[50, 50]} css={{ marginBottom: theme.spacing(0.5) }}>
              <TimeField
                name='customStartTime'
                label={getLabelWithTimezoneAbbr(t('Start Time'), locationTimezoneAbbr)}
                css={fieldStyles}
                {...startTimeField}
              />
              <TimeField
                name='customEndTime'
                label={getLabelWithTimezoneAbbr(t('End Time'), locationTimezoneAbbr)}
                css={fieldStyles}
                {...endTimeField}
              />
            </FormRow>
            {isEndTimeNextDay && (
              <Text size='small' textAlign='right' css={{ color: theme.colors.indigo50 }}>
                {t('Ends {{nextDay}} (next day)', {
                  nextDay: dayjs(`${dateField.value}`).add(1, 'day').format('MMM Do'),
                })}
              </Text>
            )}
          </>
        )}
      </form>
    </Card>
  );
};

const fieldStyles = css`
  max-width: 194px;
`;

const sourceTextStyles = css`
  & .text {
    margin-right: ${theme.spacing(1)};
  }

  & .small-text {
    font-size: ${theme.font.size.small};
    line-height: ${theme.font.lineHeight};
    color: ${theme.font.colors.subdued};
  }
`;
